import { FC } from 'react';
import { LearningTask } from '@/types/learning';

interface TaskCardProps {
  task: LearningTask;
  onStart: (taskId: string) => void;
}

const difficultyConfig = {
  beginner: { label: '入门', color: 'bg-green-100 text-green-800', icon: '⭐' },
  intermediate: { label: '进阶', color: 'bg-blue-100 text-blue-800', icon: '⭐⭐' },
  advanced: { label: '高级', color: 'bg-purple-100 text-purple-800', icon: '⭐⭐⭐' },
  expert: { label: '专家', color: 'bg-red-100 text-red-800', icon: '⭐⭐⭐⭐' }
};

const typeConfig = {
  concept: { label: '概念', icon: '📖', color: 'text-blue-600' },
  practice: { label: '实践', icon: '💻', color: 'text-green-600' },
  project: { label: '项目', icon: '🚀', color: 'text-purple-600' },
  quiz: { label: '测验', icon: '📝', color: 'text-orange-600' }
};

const statusConfig = {
  locked: { label: '未解锁', disabled: true, buttonClass: 'bg-gray-300 cursor-not-allowed' },
  available: { label: '开始学习', disabled: false, buttonClass: 'bg-primary-600 hover:bg-primary-700' },
  in_progress: { label: '继续学习', disabled: false, buttonClass: 'bg-blue-600 hover:bg-blue-700' },
  completed: { label: '已完成', disabled: false, buttonClass: 'bg-green-600 hover:bg-green-700' }
};

export const TaskCard: FC<TaskCardProps> = ({ task, onStart }) => {
  const difficulty = difficultyConfig[task.difficulty];
  const type = typeConfig[task.type];
  const status = statusConfig[task.status];

  return (
    <div
      className={`
        bg-white rounded-lg border-2 p-6 transition-all duration-300
        ${task.status === 'locked' ? 'opacity-50 border-gray-200' : 'border-gray-200 hover:border-primary-500 hover:shadow-lg'}
        ${task.status === 'completed' ? 'bg-green-50 border-green-300' : ''}
      `}
    >
      {/* 头部 */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-2xl ${type.color}`}>{type.icon}</span>
            <h3 className="text-lg font-semibold text-gray-900">{task.title}</h3>
            {task.status === 'completed' && (
              <span className="text-green-600 text-xl">✓</span>
            )}
            {task.status === 'locked' && (
              <span className="text-gray-400 text-xl">🔒</span>
            )}
          </div>
          <p className="text-sm text-gray-600">{task.description}</p>
        </div>
      </div>

      {/* 标签 */}
      <div className="flex flex-wrap gap-2 mb-4">
        {/* 类型标签 */}
        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
          {type.label}
        </span>

        {/* 难度标签 */}
        <span className={`px-2 py-1 text-xs font-medium rounded ${difficulty.color}`}>
          {difficulty.icon} {difficulty.label}
        </span>

        {/* 时间标签 */}
        <span className="px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded flex items-center gap-1">
          ⏱️ {task.estimatedTime}分钟
        </span>

        {/* 经验值标签 */}
        <span className="px-2 py-1 text-xs font-medium bg-yellow-50 text-yellow-700 rounded flex items-center gap-1">
          ✨ +{task.xpReward} XP
        </span>
      </div>

      {/* 学习目标 */}
      {task.content.objectives && task.content.objectives.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">学习目标：</h4>
          <ul className="space-y-1">
            {task.content.objectives.slice(0, 3).map((objective, index) => (
              <li key={index} className="text-sm text-gray-600 flex items-start">
                <span className="text-primary-500 mr-2">•</span>
                <span>{objective}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 前置任务提示 */}
      {task.status === 'locked' && task.prerequisites && task.prerequisites.length > 0 && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-xs text-yellow-800">
            🔒 需要先完成 {task.prerequisites.length} 个前置任务才能解锁
          </p>
        </div>
      )}

      {/* 操作按钮 */}
      <div className="mt-4">
        <button
          onClick={() => !status.disabled && onStart(task.id)}
          disabled={status.disabled}
          className={`
            w-full px-4 py-2 rounded-lg text-white font-medium text-sm
            transition-colors duration-200
            ${status.buttonClass}
            ${status.disabled ? '' : 'transform hover:scale-[1.02]'}
          `}
        >
          {status.label}
          {task.status === 'completed' && ' ✓'}
        </button>
      </div>
    </div>
  );
};
